import { useState } from "react";
import { Link } from "react-router-dom";
import { ShoppingCart, Eye, Receipt } from "lucide-react";
import { FpsBadge, PtaBadge, ConditionBadge, ChipBadge } from "@/components/Badges";
import OutboundModal from "@/components/OutboundModal";

const pkr = (n) => `PKR ${n.toLocaleString("en-PK")}`;

export default function DeviceCard({ device }) {
  const [modal, setModal] = useState(null);
  const games = device.games || {};

  return (
    <div
      data-testid={`device-card-${device.slug}`}
      className="group flex flex-col overflow-hidden rounded-xl border border-border bg-slateSurface transition-all duration-300 hover:-translate-y-1 hover:border-neon/50 hover:shadow-[0_0_24px_rgba(57,255,20,0.15)]"
    >
      <Link to={`/device/${device.slug}`} className="relative block aspect-[4/3] overflow-hidden bg-graphite">
        <img
          src={device.image}
          alt={`${device.name} gaming phone`}
          loading="lazy"
          className="h-full w-full object-contain p-3 opacity-95 transition-transform duration-500 group-hover:scale-105"
        />
        <div className="absolute left-2 top-2 flex gap-1.5">
          <FpsBadge fps={device.fps_tier} />
        </div>
        <div className="absolute right-2 top-2">
          <span className="rounded-md bg-black/70 px-2 py-0.5 text-xs font-heading text-slate-200">{device.brand}</span>
        </div>
      </Link>

      <div className="flex flex-1 flex-col p-4">
        <Link to={`/device/${device.slug}`}>
          <h3 className="font-heading text-base font-bold text-white transition-colors group-hover:text-neon">
            {device.name}
          </h3>
        </Link>

        <div className="mt-2 flex flex-wrap gap-1.5">
          <ChipBadge chipset={device.chipset} />
        </div>
        <p className="mt-1.5 text-xs text-slate-400">{device.ram_storage}</p>

        <div className="mt-3 rounded-lg border border-border bg-graphite p-2.5">
          <div className="text-[10px] uppercase tracking-wider text-slate-500">Live price (PKR)</div>
          <div className="font-display text-sm font-bold text-neon">
            {device.price_min === device.price_max ? pkr(device.price_min) : `${pkr(device.price_min)} - ${pkr(device.price_max)}`}
          </div>
        </div>

        <div className="mt-3 space-y-1 text-xs text-slate-300">
          <div><span className="text-slate-500">PUBG:</span> {games.pubg}</div>
          <div><span className="text-slate-500">Thermals:</span> {device.thermal_label}</div>
          <div><span className="text-slate-500">Gyro:</span> {device.gyro_label}</div>
        </div>

        <div className="mt-3 flex flex-wrap gap-1.5">
          {(device.conditions || []).slice(0, 2).map((c) => (
            <ConditionBadge key={c} label={c} />
          ))}
          <PtaBadge status={device.pta_status} />
        </div>

        <div className="mt-4 grid grid-cols-1 gap-2">
          <Link
            data-testid={`view-details-${device.slug}`}
            to={`/device/${device.slug}`}
            className="flex items-center justify-center gap-2 rounded-lg border border-border bg-graphite px-3 py-2 text-xs font-heading font-semibold text-white transition-all hover:border-cyber/50"
          >
            <Eye className="h-3.5 w-3.5" /> View Details
          </Link>
          <div className="grid grid-cols-2 gap-2">
            <button
              data-testid={`buy-partner-${device.slug}`}
              onClick={() => setModal("marketplace")}
              className="flex items-center justify-center gap-1.5 rounded-lg bg-neon px-2 py-2 text-xs font-heading font-bold text-charcoal transition-all hover:shadow-[0_0_18px_rgba(57,255,20,0.5)]"
            >
              <ShoppingCart className="h-3.5 w-3.5" /> Buy
            </button>
            <button
              data-testid={`pta-tax-${device.slug}`}
              onClick={() => setModal("pta")}
              className="flex items-center justify-center gap-1.5 rounded-lg border border-cyber/40 bg-cyber/10 px-2 py-2 text-xs font-heading font-bold text-cyber transition-all hover:bg-cyber/20"
            >
              <Receipt className="h-3.5 w-3.5" /> PTA Tax
            </button>
          </div>
        </div>
      </div>

      <OutboundModal open={!!modal} mode={modal} deviceName={device.name} deviceBrand={device.brand} onClose={() => setModal(null)} />
    </div>
  );
}
